/**
 * Withdrawal retention section for the agent call guide.
 *
 * Business goal: understand why the client wants to withdraw and offer a
 * partial withdrawal; if they insist, respect it and set the right expectation
 * about the wait on a new destination account. The agent never names the day
 * the money arrives (owner, 2026-09-25) and never tells the client why a
 * control exists.
 *
 * The wait is N business days where N comes from platform.settings
 * (`withdrawal.cooling_off_business_days`) — NEVER hardcoded — surfaced via
 * the retention-context endpoint so copy always matches the live config.
 *
 * Display:
 *   · First withdrawal (no prior): the static script that explains the hold.
 *   · Repeat client WITH an open withdrawal: the live banner with the exact
 *     release date for each in-flight request.
 * Either way the retention talking-points are shown.
 *
 * Fail-quiet: a load error renders nothing (never blocks the call guide).
 */
import { useEffect, useState } from 'react';
import withdrawalsService from '../../services/withdrawals.service';
import { formatDate, formatMoney } from '../../lib/datetime';
import { humanize } from '../../lib/stringCase';
import { colors } from '../../styles/theme';
import type { WithdrawalRetentionContext } from '../../types/finance';

const RETENTION_POINTS: readonly string[] = [
  'Ask why they want to withdraw — a one-off liquidity need, or something we can solve?',
  'Tell them the day after which the current term of their capital is complete, and that the next term starts by itself unless they withdraw. Withdrawn on that day or before, it earns nothing for that term.',
  'Offer a partial withdrawal instead of a full one; a partial withdrawal takes their oldest contribution first. If they want a shorter term, explain first that leaving now earns nothing for this term, and that the early withdrawal charge applies where there is one.',
  'If they ask who receives what: the result of a term left early belongs to a QMI company that holds the voting shares; the early withdrawal charge, where there is one, is a penalty for ending a term early and is paid to QMI. Their screen shows the exact amount before they confirm.',
  "Never name the day the money arrives. Only once it shows as scheduled, say: 'Your withdrawal is scheduled. Transfers are sent on business days, and how long one takes depends on the country your money is held in and the country of the account it is going to, so it can take a few days.' Before that, tell them only the status they see.",
  'Money goes back only to a bank account of their own that a transfer from it has already verified.',
  'If they still decide to withdraw, respect it, log it, and escalate to a manager — no pressure (fair treatment).',
];

function RetentionPoint({ text }: { readonly text: string }): JSX.Element {
  const [checked, setChecked] = useState(false);
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', cursor: 'pointer', opacity: checked ? 0.45 : 1, transition: 'opacity 0.15s' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => setChecked(e.target.checked)}
        style={{ marginTop: 3, accentColor: colors.navy, flexShrink: 0 }}
      />
      <span style={{ fontSize: 13, color: colors.inkSoft, textDecoration: checked ? 'line-through' : 'none' }}>
        {text}
      </span>
    </label>
  );
}

export default function WithdrawalRetentionGuide({ clientId }: { readonly clientId: string }): JSX.Element | null {
  const [ctx, setCtx] = useState<WithdrawalRetentionContext | null>(null);

  useEffect(() => {
    let cancelled = false;
    withdrawalsService.retentionContext(clientId)
      .then(c => { if (!cancelled) setCtx(c); })
      .catch(() => { /* fail-quiet: agent aid, never blocks the guide */ });
    return () => { cancelled = true; };
  }, [clientId]);

  if (!ctx) return null;

  const n = ctx.cooling_off_business_days;
  // Live banner only for repeat clients with an in-flight request; the first
  // withdrawal gets the static educational script (per product decision).
  const showLive = ctx.has_prior_withdrawals && ctx.open_withdrawals.length > 0;

  // Said to the client: the wait, never the reason for it or the steps behind it.
  const staticScript =
    `If you recently added or changed the account the money goes to, there is a wait of ${n} ` +
    `business days before it can be sent.`;

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        Withdrawals &amp; retention
      </div>

      {showLive ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {ctx.open_withdrawals.map(w => {
            const live = w.within_cooling_off && w.cooling_off_clears_on
              ? `A manager can approve it from ${formatDate(w.cooling_off_clears_on)}: ${n} business day(s) after ` +
                `the destination account was added or changed. This is not the day the money arrives — never name one.`
              : 'Past the security hold — a manager can approve it now.';
            return (
              <div key={w.id} style={{ background: colors.warningBg, border: '1px solid #FCD34D', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.ink, marginBottom: 3 }}>
                  Withdrawal in progress: {formatMoney(w.amount, w.currency_code)}
                  {w.destination_account_last4 ? ` → ••••${w.destination_account_last4}` : ''} · {humanize(w.status)}
                </div>
                <div style={{ fontSize: 12, color: colors.inkSoft }}>{live}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: `${colors.navy}08`, borderLeft: `3px solid ${colors.navy}`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13, color: colors.inkSoft }}>
          {staticScript}
        </div>
      )}

      <div>
        {RETENTION_POINTS.map(t => <RetentionPoint key={t} text={t} />)}
      </div>
    </div>
  );
}
